const PI = Math.PI

export function modulate(value, rangeA, rangeB, clamp = false) {
    const [fromLow, fromHigh] = rangeA
    const [toLow, toHigh] = rangeB
    const result = toLow + ((value - fromLow) / (fromHigh - fromLow)) * (toHigh - toLow)
    if (clamp === true) {
        if (toLow < toHigh) {
            if (result < toLow) {
                return toLow
            }
            if (result > toHigh) {
                return toHigh
            }
        } else {
            if (result > toLow) {
                return toLow
            }
            if (result < toHigh) {
                return toHigh
            }
        }
    }
    return result
}

export function rotatePoint(x, y, cx, cy, angle) {
    const s = Math.sin(angle)
    const c = Math.cos(angle)

    const px = x - cx
    const py = y - cy

    const nx = px * c - py * s
    const ny = px * s + py * c

    return [nx + cx, ny + cy]
}

export function getDistance(x0, y0, x1, y1) {
    return Math.hypot(y1 - y0, x1 - x0)
}

export function getAngle(x0, y0, x1, y1) {
    return Math.atan2(y1 - y0, x1 - x0)
}

export function projectPoint(x0, y0, a, d) {
    return [Math.cos(a) * d + x0, Math.sin(a) * d + y0]
}

export function getPointBetween(x0, y0, x1, y1, d = 0.5) {
    return [x0 + (x1 - x0) * d, y0 + (y1 - y0) * d]
}

export function getSector(a, s = 8) {
    return Math.floor(s * (0.5 + ((a / (PI * 2)) % s)))
}

export function getAngliness(x0, y0, x1, y1) {
    return Math.abs((x1 - x0) / 2 / ((y1 - y0) / 2))
}

export function getEllipseRectangleIntersectionPoints(x, y, w, h, cx, cy, rx, ry, angle) {
    let points = []

    for (let [px0, py0, px1, py1] of [
        [x, y, x + w, y],
        [x + w, y, x + w, y + h],
        [x + w, y + h, x, y + h],
        [x, y + h, x, y],
    ]) {
        const ints = getEllipseSegmentIntersections(px0, py0, px1, py1, cx, cy, rx, ry, angle)

        if (ints.length > 0) {
            points.push(...ints)
        }
    }

    points = points.sort(([x0, y0], [x1, y1]) => {
        return Math.sin(getAngle(cx, cy, x0, y0) - getAngle(cx, cy, x1, y1)) > 0 ? -1 : 1
    })

    return points
}

export function getEllipseSegmentIntersections(x0, y0, x1, y1, cx, cy, rx, ry, rotation = 0, segment_only = true) {
    if (rx === 0 || ry === 0 || (x0 === x1 && y0 === y1)) {
        return []
    }

    rx = rx < 0 ? rx : -rx
    ry = ry < 0 ? ry : -ry

    if (rotation !== 0) {
        ;[x0, y0] = rotatePoint(x0, y0, cx, cy, -rotation)
        ;[x1, y1] = rotatePoint(x1, y1, cx, cy, -rotation)
    }

    x0 -= cx
    y0 -= cy
    x1 -= cx
    y1 -= cy

    var A = ((x1 - x0) * (x1 - x0)) / rx / rx + ((y1 - y0) * (y1 - y0)) / ry / ry
    var B = (2 * x0 * (x1 - x0)) / rx / rx + (2 * y0 * (y1 - y0)) / ry / ry
    var C = (x0 * x0) / rx / rx + (y0 * y0) / ry / ry - 1

    var tValues = []

    var discriminant = B * B - 4 * A * C

    if (discriminant === 0) {
        tValues.push(-B / 2 / A)
    } else if (discriminant > 0) {
        tValues.push((-B + Math.sqrt(discriminant)) / 2 / A)
        tValues.push((-B - Math.sqrt(discriminant)) / 2 / A)
    }

    return tValues

        .filter((t) => !segment_only || (t >= 0 && t <= 1))

        .map((t) => [x0 + (x1 - x0) * t + cx, y0 + (y1 - y0) * t + cy])

        .map((p) => (rotation === 0 ? p : rotatePoint(p[0], p[1], cx, cy, rotation)))
}

export function doRectanglesCollide(x0, y0, w0, h0, x1, y1, w1, h1) {
    return !(x0 >= x1 + w1 || x1 >= x0 + w0 || y0 >= y1 + h1 || y1 >= y0 + h0)
}

export function getSegmentRectangleIntersectionPoints(x0, y0, x1, y1, x, y, w, h) {
    let points = []

    for (let [px0, py0, px1, py1] of [
        [x, y, x + w, y],
        [x + w, y, x + w, y + h],
        [x + w, y + h, x, y + h],
        [x, y + h, x, y],
    ]) {
        const ints = getSegmentSegmentIntersection(px0, py0, px1, py1, x0, y0, x1, y1)
        if (ints) {
            points.push(ints)
        }
    }

    return points
}

export function getSegmentSegmentIntersection(x0, y0, x1, y1, x2, y2, x3, y3) {
    const denom = (y3 - y2) * (x1 - x0) - (x3 - x2) * (y1 - y0)
    const numeA = (x3 - x2) * (y0 - y2) - (y3 - y2) * (x0 - x2)
    const numeB = (x1 - x0) * (y0 - y2) - (y1 - y0) * (x0 - x2)

    if (denom === 0) {
        if (numeA === 0 && numeB === 0) {
            return undefined
        }
        return undefined
    }

    const uA = numeA / denom
    const uB = numeB / denom

    if (uA >= 0 && uA <= 1 && uB >= 0 && uB <= 1) {
        return [x0 + uA * (x1 - x0), y0 + uA * (y1 - y0)]
    }

    return undefined
}

export function getSegmentRoundedRectangleIntersectionPoints(x0, y0, x1, y1, x, y, w, h, r) {
    const mx = x + w,
        my = y + h,
        rx = x + r,
        ry = y + r,
        mrx = x + w - r,
        mry = y + h - r

    const segments = [
        [x, mry, x, ry, x, y],
        [rx, y, mrx, y, mx, y],
        [mx, ry, mx, mry, mx, my],
        [mrx, my, rx, my, x, my],
    ]

    const corners = [
        [rx, ry, PI, PI * 1.5],
        [mrx, ry, PI * 1.5, PI * 2],
        [mrx, mry, 0, PI * 0.5],
        [rx, mry, PI * 0.5, PI],
    ]

    let points = []

    segments.forEach((segment, i) => {
        const [px0, py0, px1, py1] = segment
        const [cx, cy, as, ae] = corners[i]

        getSegmentCircleIntersections(cx, cy, r, x0, y0, x1, y1)
            .filter((pt) => {
                const pointAngle = normalizeAngle(getAngle(cx, cy, pt[0], pt[1]))
                return pointAngle > as && pointAngle < ae
            })
            .forEach((pt) => points.push(pt))

        const segmentInt = getSegmentSegmentIntersection(x0, y0, x1, y1, px0, py0, px1, py1)

        if (!!segmentInt) {
            points.push(segmentInt)
        }
    })

    return points
}

export function getSegmentCircleIntersections(cx, cy, r, x0, y0, x1, y1) {
    var b,
        c,
        d,
        u1,
        u2,
        ret,
        retP1,
        retP2,
        v1 = [x1 - x0, y1 - y0],
        v2 = [x0 - cx, y0 - cy]

    b = v1[0] * v2[0] + v1[1] * v2[1]
    c = 2 * (v1[0] * v1[0] + v1[1] * v1[1])
    b *= -2
    d = Math.sqrt(b * b - 2 * c * (v2[0] * v2[0] + v2[1] * v2[1] - r * r))
    if (isNaN(d)) {
        return []
    }
    u1 = (b - d) / c
    u2 = (b + d) / c
    retP1 = []
    retP2 = []
    ret = []

    if (u1 <= 1 && u1 >= 0) {
        retP1[0] = x0 + v1[0] * u1
        retP1[1] = y0 + v1[1] * u1
        ret[0] = retP1
    }

    if (u2 <= 1 && u2 >= 0) {
        retP2[0] = x0 + v1[0] * u2
        retP2[1] = y0 + v1[1] * u2
        ret[ret.length] = retP2
    }

    return ret
}

export function normalizeAngle(radians) {
    return radians - PI * 2 * Math.floor(radians / (PI * 2))
}

export function getRayRectangleIntersectionPoints(ox, oy, dx, dy, x, y, w, h) {
    let points = []

    for (let [px0, py0, px1, py1] of [
        [x, y, x + w, y],
        [x + w, y, x + w, y + h],
        [x + w, y + h, x, y + h],
        [x, y + h, x, y],
    ]) {
        const ints = getRaySegmentIntersection(ox, oy, dx, dy, px0, py0, px1, py1)
        if (ints) {
            points.push(ints)
        }
    }

    return points
}

export function getRaySegmentIntersection(x, y, dx, dy, x0, y0, x1, y1) {
    let r, s, d

    if (dy * (x1 - x0) !== dx * (y1 - y0)) {
        d = dx * (y1 - y0) - dy * (x1 - x0)
        if (d !== 0) {
            r = ((y - y0) * (x1 - x0) - (x - x0) * (y1 - y0)) / d
            s = ((y - y0) * dx - (x - x0) * dy) / d
            if (r >= 0 && s >= 0 && s <= 1) {
                return [x + r * dx, y + r * dy]
            }
        }
    }
    return undefined
}

export function getDelta(angle) {
    return [Math.cos(angle), Math.sin(angle)]
}

export function getIntermediate(angle) {
    return Math.abs(Math.abs(angle % (PI / 2)) - PI / 4) / (PI / 4)
}

export function getLineBetweenRoundedRectangles(x0, y0, w0, h0, r0, x1, y1, w1, h1, r1) {
    const cx0 = x0 + w0 / 2,
        cy0 = y0 + h0 / 2,
        cx1 = x1 + w1 / 2,
        cy1 = y1 + h1 / 2,
        [[di0x, di0y]] = getRayRoundedRectangleIntersection(cx0, cy0, cx1 - cx0, cy1 - cy0, x0, y0, w0, h0, r0) || [
            [cx0, cy0],
        ],
        [[di1x, di1y]] = getRayRoundedRectangleIntersection(cx1, cy1, cx0 - cx1, cy0 - cy1, x1, y1, w1, h1, r1) || [
            [cx1, cy1],
        ]

    return [di0x, di0y, di1x, di1y]
}

export function getRayRoundedRectangleIntersection(ox, oy, dx, dy, x, y, w, h, r) {
    const mx = x + w,
        my = y + h,
        rx = x + r - 1,
        ry = y + r - 1,
        mrx = x + w - r + 1,
        mry = y + h - r + 1

    const segments = [
        [x, mry, x, ry],
        [rx, y, mrx, y],
        [mx, ry, mx, mry],
        [mrx, my, rx, my],
    ]

    const corners = [
        [rx, ry, Math.PI, Math.PI * 1.5],
        [mrx, ry, Math.PI * 1.5, Math.PI * 2],
        [mrx, mry, 0, Math.PI * 0.5],
        [rx, mry, Math.PI * 0.5, Math.PI],
    ]

    let points = []

    segments.forEach((segment, i) => {
        const [px0, py0, px1, py1] = segment
        const [cx, cy, as, ae] = corners[i]

        const intersections = getRayCircleIntersection(cx, cy, r, ox, oy, dx, dy)

        intersections &&
            intersections
                .filter((pt) => {
                    const pointAngle = normalizeAngle(getAngle(cx, cy, pt[0], pt[1]))
                    return pointAngle > as && pointAngle < ae
                })
                .forEach((pt) => points.push(pt))

        const segmentInt = getRaySegmentIntersection(ox, oy, dx, dy, px0, py0, px1, py1)

        if (!!segmentInt) {
            points.push(segmentInt)
        }
    })

    return points
}

export function getRectangleSegmentIntersectedByRay(x, y, w, h, ox, oy, dx, dy) {
    return getRectangleSegments(x, y, w, h).find(([sx0, sy0, sx1, sy1]) =>
        getRaySegmentIntersection(ox, oy, dx, dy, sx0, sy0, sx1, sy1)
    )
}

export function getRectangleSegments(x, y, w, h) {
    return [
        [x, y, x + w, y],
        [x + w, y, x + w, y + h],
        [x + w, y + h, x, y + h],
        [x, y + h, x, y],
    ]
}

export function getRoundedRectangleSegments(x, y, w, h, r) {
    const rx = x + r,
        ry = y + r,
        mx = x + w,
        my = y + h,
        mrx = x + w - r,
        mry = y + h - r

    return [
        [x, mry, x, ry, x, y],
        [rx, y, mrx, y, mx, y],
        [mx, ry, mx, mry, mx, my],
        [mrx, my, rx, my, x, my],
    ]
}

export function getRayCircleIntersection(cx, cy, r, ox, oy, dx, dy) {
    return getSegmentCircleIntersections(cx, cy, r, ox, oy, dx * 999999, dy * 999999)
}

export function getArrow(x0, y0, x1, y1, options: any = {}) {
    const {
        bow = 0,
        stretch = 0.5,
        stretchMin = 0,
        stretchMax = 420,
        padStart = 0,
        padEnd = 0,
        flip = false,
        straights = true,
    } = options

    const angle = getAngle(x0, y0, x1, y1)
    const dist = getDistance(x0, y0, x1, y1)
    const angliness = getAngliness(x0, y0, x1, y1)

    if (
        dist < (padStart + padEnd) * 2 ||
        (bow === 0 && stretch === 0) ||
        (straights && [0, 1, Infinity].includes(angliness))
    ) {
        const ps = Math.max(0, Math.min(dist - padStart, padStart))
        const pe = Math.max(0, Math.min(dist - ps, padEnd))

        let [px0, py0] = projectPoint(x0, y0, angle, ps)

        let [px1, py1] = projectPoint(x1, y1, angle + Math.PI, pe)

        const [mx, my] = getPointBetween(px0, py0, px1, py1, 0.5)

        return [px0, py0, mx, my, px1, py1, angle, angle, angle]
    }

    let rot = (getSector(angle) % 2 === 0 ? 1 : -1) * (flip ? -1 : 1)

    const arc = bow + modulate(dist, [stretchMin, stretchMax], [1, 0], true) * stretch

    const [mx, my] = getPointBetween(x0, y0, x1, y1, 0.5)

    let [cx, cy] = getPointBetween(x0, y0, x1, y1, 0.5 - arc)

    ;[cx, cy] = rotatePoint(cx, cy, mx, my, (Math.PI / 2) * rot)

    const a0 = getAngle(x0, y0, cx, cy)
    const [px0, py0] = projectPoint(x0, y0, a0, padStart)

    const a1 = getAngle(x1, y1, cx, cy)
    const [px1, py1] = projectPoint(x1, y1, a1, padEnd)

    const as = getAngle(cx, cy, x0, y0)

    const ae = getAngle(cx, cy, x1, y1)

    const [mx1, my1] = getPointBetween(px0, py0, px1, py1, 0.5)

    let [cx1, cy1] = getPointBetween(px0, py0, px1, py1, 0.5 - arc)

    ;[cx1, cy1] = rotatePoint(cx1, cy1, mx1, my1, (Math.PI / 2) * rot)

    let [cx2, cy2] = getPointBetween(cx, cy, cx1, cy1, 0.5)

    return [px0, py0, cx2, cy2, px1, py1, ae, as, angle]
}
